// Display helpers for the weekly update: week labels and date stepping, the narrative split into sections and
// citation segments, where a citation links to, and the update as plain text. No business math — the movement,
// the glance, and the week ahead all arrive computed from the API (ADR 0003 §4).
import type { Citation, CitationKind, WeeklyView, WeekWindow } from '../types/weekly'
import { formatMoney } from './format'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** YYYY-MM-DD → {y, m, d} without touching the local time zone. */
function parts(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return { y, m, d }
}

/** "Sep 7 to 13, 2026" or "Sep 28 to Oct 4, 2026". */
export function weekLabel(w: WeekWindow): string {
  const a = parts(w.start)
  const b = parts(w.end)
  const sameMonth = a.y === b.y && a.m === b.m
  return sameMonth ? `${MONTHS[a.m - 1]} ${a.d} to ${b.d}, ${b.y}` : `${MONTHS[a.m - 1]} ${a.d} to ${MONTHS[b.m - 1]} ${b.d}, ${b.y}`
}

/** "Mon Sep 7" style, from YYYY-MM-DD, time-zone safe. */
export function dayLabel(iso: string): string {
  const p = parts(iso)
  const day = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]} ${MONTHS[p.m - 1]} ${p.d}`
}

/** Step a YYYY-MM-DD by whole days (for prev/next week). */
export function shiftDays(iso: string, days: number): string {
  const p = parts(iso)
  const t = new Date(Date.UTC(p.y, p.m - 1, p.d + days))
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`
}

export type Segment = { type: 'text'; text: string } | { type: 'cite'; keys: string[] }
export interface NarrativeSection { title: string | null; paragraphs: Segment[][] }

const CITE = /\[([A-Z]\d{1,3}(?:\s*,\s*[A-Z]\d{1,3})*)\]/g

/** A line of prose into text and citation segments: "moved to proposal [E1, M2]." */
export function segments(line: string): Segment[] {
  const out: Segment[] = []
  let last = 0
  for (const m of line.matchAll(CITE)) {
    const at = m.index ?? 0
    if (at > last) out.push({ type: 'text', text: line.slice(last, at) })
    out.push({ type: 'cite', keys: m[1].split(',').map((k) => k.trim()) })
    last = at + m[0].length
  }
  if (last < line.length) out.push({ type: 'text', text: line.slice(last) })
  return out
}

/** The narrative as sections: a '# ' line opens one; blank lines separate paragraphs. */
export function parseNarrative(text: string): NarrativeSection[] {
  const sections: NarrativeSection[] = []
  let current: NarrativeSection | null = null
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (line.startsWith('# ')) { current = { title: line.slice(2).trim(), paragraphs: [] }; sections.push(current); continue }
    if (line.length === 0) continue
    if (!current) { current = { title: null, paragraphs: [] }; sections.push(current) }
    current.paragraphs.push(segments(line))
  }
  return sections
}

/** Where a cited fact lives in the app. */
export function routeFor(kind: CitationKind, id: string): string {
  switch (kind) {
    case 'account': return `/accounts/${encodeURIComponent(id)}`
    case 'person': return '/contacts'
    case 'meeting': return '/meetings'
    case 'tactic': return '/playbook'
    default: return '/opportunities'
  }
}

/** The whole update as plain text, for copy and export. Citations stay as written. */
export function exportText(v: WeeklyView): string {
  const m = v.movement
  const g = m.glance
  const lines: string[] = []
  lines.push(`WEEKLY UPDATE - week of ${weekLabel(m.week)}`)
  lines.push(`Measured from ${m.measuredFrom.threadsActive} active threads, ${m.measuredFrom.meetingsHeld} external meetings, ${m.measuredFrom.signalsRaised} signals.`)
  lines.push('')
  lines.push('AT A GLANCE')
  lines.push(`New pursuits ${g.newPursuits} | Stage up ${g.stageUps} | Stage down ${g.stageDowns} | Named this week ${formatMoney(g.amountNamed)} | Meetings held ${g.meetingsHeld} | Resurfaced ${g.dormantResurfaced} | Tactics applied ${g.tacticsApplied} (${g.tacticResults} with a result) | Next steps owed ${g.nextStepsOwed}`)
  lines.push(`Pipeline named ${formatMoney(g.pipelineNow)}${g.pipelineBefore != null ? ` (was ${formatMoney(g.pipelineBefore)} at the last update)` : ''} across ${g.openPursuits} open pursuits`)
  lines.push('')
  if (v.narrative) {
    lines.push(v.narrative.modelWritten ? `NARRATIVE (written ${new Date(v.narrative.writtenAt).toLocaleDateString('en-US')})` : 'NARRATIVE (laid out from the facts; no model)')
    lines.push(v.narrative.text)
    lines.push('')
    const cited = v.narrative.citations
    if (cited.length) { lines.push('CITED'); for (const c of cited) lines.push(`${c.key}  ${c.kind}: ${c.label}`); lines.push('') }
  }
  for (const s of m.slices) {
    const items = m.items.filter((i) => i.slice === s.slice)
    if (items.length === 0) continue
    lines.push(s.title.toUpperCase())
    for (const i of items) {
      lines.push(`${i.key}  ${i.title}${i.subtitle ? ` (${i.subtitle})` : ''}: ${i.callout}${i.stageFrom && i.stageTo && i.stageFrom !== i.stageTo ? `, ${i.stageFrom} to ${i.stageTo}` : ''}${i.amount ? `, ${formatMoney(i.amount)}` : ''}`)
      for (const e of i.evidence) lines.push(`    - ${e.label}${e.on ? ` (${dayLabel(e.on)})` : ''}`)
    }
    lines.push('')
  }
  const a = m.ahead
  lines.push(`WEEK AHEAD - ${weekLabel(a.window)}`)
  for (const mt of a.meetings) lines.push(`${mt.key}  ${dayLabel(mt.start.slice(0, 10))}: ${mt.subject}${mt.companyName ? ` with ${mt.companyName}` : ''}`)
  for (const st of a.steps) lines.push(`${st.key}  ${st.mine ? 'Owed' : 'Awaited'}: ${st.nextStep} (${st.subject}, ${st.companyName})${st.by ? ` by ${dayLabel(st.by)}` : ''}${st.overdue ? ' OVERDUE' : ''}`)
  for (const t of a.tactics) lines.push(`Tactic: ${t.tacticName} on ${t.subject} (${t.why})`)
  for (const c of a.contacts) lines.push(`Touch: ${c.name}, ${c.companyName} (${c.dormancyDays} days quiet)`)
  return lines.join('\n').trimEnd()
}

export function citationByKey(citations: Citation[]): Map<string, Citation> {
  return new Map(citations.map((c) => [c.key, c]))
}
